var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var pixelConverter = function (val) { return "".concat(val.trim(), "px"); };
var clampConverter = function (value) {
    var parts = value.split(',').map(function (part) { return part.trim(); });
    var _a = parts[0], min = _a === void 0 ? '14' : _a, _b = parts[1], middle = _b === void 0 ? value : _b, _c = parts[2], max = _c === void 0 ? '32' : _c;
    if (parts.length !== 3) {
        return "clamp(14px, ".concat(middle, "vw, 32px)");
    }
    return "clamp(".concat(min, "px, ").concat(middle, "vw, ").concat(max, "px)");
};
var convertSetting = function (obj, converter) {
    return Object.keys(obj).reduce(function (acc, key) {
        acc[key] = obj[key] ? converter(obj[key]) : null;
        return acc;
    }, {});
};
var convertSizeSetting = function (obj, relativeToBrowserWidth) {
    var converterFunction = relativeToBrowserWidth
        ? clampConverter
        : pixelConverter;
    return convertSetting(obj, converterFunction);
};
var convertLetterSpacing = function (obj) {
    return convertSetting(obj, function (value) {
        var trimmedVal = Number(value);
        return "".concat(trimmedVal / 100, "em");
    });
};
var convertSpaceAfter = function (obj) {
    return convertSetting(obj, pixelConverter);
};
var keyMap = {
    headerSize: convertSizeSetting,
    eyebrowSize: convertSizeSetting,
    bodySize: convertSizeSetting,
    headerLetterSpacing: convertLetterSpacing,
    eyebrowLetterSpacing: convertLetterSpacing,
    bodyLetterSpacing: convertLetterSpacing,
    headerSpaceAfter: convertSpaceAfter,
    eyebrowSpaceAfter: convertSpaceAfter,
    bodySpaceAfter: convertSpaceAfter
};
/**
 * Applies CSS units to the style settings based on the provided API settings.
 * @param settings - The API settings to apply CSS units to.
 * @returns The modified API settings with CSS units applied.
 */
export function applyCssUnitsToStyleSettings(settings) {
    var modifiedSettings = settings.map(function (setting) {
        var modifiedSetting = __assign({}, setting);
        Object.keys(modifiedSetting).forEach(function (key) {
            if (keyMap[key] && typeof keyMap[key] === 'function') {
                modifiedSetting[key] = keyMap[key](modifiedSetting[key], modifiedSetting.relativeToBrowserWidth);
            }
        });
        return modifiedSetting;
    });
    return modifiedSettings;
}
/**
 * Merges two theme configurations.
 * @param obj1 - The first theme configuration object.
 * @param obj2 - The second theme configuration object.
 * @returns The merged theme configuration object.
 */
export function mergeConfigs(obj1, obj2) {
    var merged = __assign({}, obj1);
    Object.keys(obj2).forEach(function (key) {
        if (!Object.prototype.hasOwnProperty.call(obj1, key)) {
            return;
        }
        var typedKey = key;
        if (typeof obj2[typedKey] === 'object' &&
            obj2[typedKey] !== null &&
            !Array.isArray(obj2[typedKey])) {
            if (typeof merged[typedKey] === 'object' && merged[typedKey] !== null) {
                merged[typedKey] = mergeConfigs(merged[typedKey], obj2[typedKey]);
            }
            else {
                merged[typedKey] = obj2[typedKey];
            }
        }
        else {
            merged[typedKey] = obj2[typedKey];
        }
    });
    return merged;
}
/**
 * Deletes empty or null keys from an object recursively.
 * @param obj - The object to delete empty or null keys from.
 * @returns The object with empty or null keys deleted.
 */
export function deleteEmptyOrNullKeys(obj) {
    var clonedObj = __assign({}, obj);
    Object.keys(clonedObj).forEach(function (key) {
        if (key === '__typename') {
            delete clonedObj[key];
        }
        else if (clonedObj[key] && typeof clonedObj[key] === 'object') {
            clonedObj[key] = deleteEmptyOrNullKeys(clonedObj[key]);
            // If the object becomes empty or only has __typename, delete it
            var remainingKeys = Object.keys(clonedObj[key]);
            if (remainingKeys.length === 0 ||
                (remainingKeys.length === 1 && remainingKeys[0] === '__typename')) {
                delete clonedObj[key];
            }
        }
        else if (clonedObj[key] === '' || clonedObj[key] === null) {
            delete clonedObj[key];
        }
    });
    return clonedObj;
}
/**
 * Transforms the API settings into a theme configuration object.
 * @param settings - The API settings to transform.
 * @returns The transformed theme configuration object.
 */
export function transformStyleSettings(settings) {
    var processedSettings = settings.reduce(function (acc, setting) {
        var _a;
        var contentType = setting.contentType, affectedHeader = setting.affectedHeader, eyebrowSize = setting.eyebrowSize, eyebrowLetterSpacing = setting.eyebrowLetterSpacing, eyebrowLineHeight = setting.eyebrowLineHeight, eyebrowSpaceAfter = setting.eyebrowSpaceAfter, rest = __rest(setting, ["contentType", "affectedHeader", "eyebrowSize", "eyebrowLetterSpacing", "eyebrowLineHeight", "eyebrowSpaceAfter"]);
        // Handle headerStyle settings
        if (contentType === 'headerStyle') {
            if (affectedHeader === 'ALL_HEADERS') {
                acc.headerStyle = {
                    h1: rest,
                    h2: rest,
                    h3: rest,
                    h4: rest,
                    h5: rest,
                    h6: rest
                };
            }
            else if (affectedHeader) {
                acc.headerStyle = __assign(__assign({}, acc.headerStyle), (_a = {}, _a[affectedHeader.toLowerCase()] = rest, _a));
            }
            if (eyebrowSize || eyebrowLetterSpacing || eyebrowLineHeight || eyebrowSpaceAfter) {
                acc.eyebrowStyle = __assign(__assign(__assign(__assign({}, (eyebrowSize && { eyebrowSize: eyebrowSize })), (eyebrowLetterSpacing && { eyebrowLetterSpacing: eyebrowLetterSpacing })), (eyebrowLineHeight && { eyebrowLineHeight: eyebrowLineHeight })), (eyebrowSpaceAfter && { eyebrowSpaceAfter: eyebrowSpaceAfter }));
            }
        }
        // Handle bodyStyle settings
        if (contentType === 'bodyStyle') {
            acc.bodyStyle = rest;
        }
        return acc;
    }, {});
    return {
        typography: __assign(__assign(__assign({}, processedSettings.headerStyle), (processedSettings.eyebrowStyle && { eyebrow: processedSettings.eyebrowStyle })), (processedSettings.bodyStyle && { body: processedSettings.bodyStyle }))
    };
}
